import { useRef, type TouchEvent } from 'react';

/** How far a finger has to travel sideways before a swipe turns the part. */
const MIN_DISTANCE = 72;

/** Sideways has to win clearly: a swipe that is half scroll is a scroll. */
const MAX_SLOPE = 0.5;

/** A swipe is a flick, not a slow drag across a photo. */
const MAX_DURATION_MS = 600;

/**
 * Touches that start this close to the edge of the screen belong to the
 * browser: on an iPhone that is "back" and "forward" in Safari.
 */
const EDGE = 24;

interface Start {
    x: number;
    y: number;
    at: number;
}

export interface PartSwipeHandlers {
    onTouchStart: (event: TouchEvent<HTMLElement>) => void;
    onTouchEnd: (event: TouchEvent<HTMLElement>) => void;
}

/**
 * Swipe right for the previous main part, left for the next one.
 *
 * Asked for by a surveyor who notices a wrong answer one part later and wants
 * to be back there without looking for a button. The gesture only counts when
 * it is plainly sideways and quick, starts away from the screen edge (where the
 * browser keeps its own back gesture), and does not start on something that
 * scrolls sideways or takes text — a photo strip, a text field.
 */
export function usePartSwipe(onPrevious: () => void, onNext: () => void): PartSwipeHandlers {
    const start = useRef<Start | null>(null);

    return {
        onTouchStart: (event) => {
            const touch = event.touches[0];

            if (!touch || event.touches.length > 1 || startsOnOwnGesture(event.target)) {
                start.current = null;

                return;
            }

            const width = window.innerWidth;

            start.current =
                touch.clientX < EDGE || touch.clientX > width - EDGE
                    ? null
                    : { x: touch.clientX, y: touch.clientY, at: Date.now() };
        },
        onTouchEnd: (event) => {
            const from = start.current;
            const touch = event.changedTouches[0];
            start.current = null;

            if (!from || !touch) {
                return;
            }

            const dx = touch.clientX - from.x;
            const dy = touch.clientY - from.y;

            if (
                Math.abs(dx) < MIN_DISTANCE ||
                Math.abs(dy) > Math.abs(dx) * MAX_SLOPE ||
                Date.now() - from.at > MAX_DURATION_MS
            ) {
                return;
            }

            if (dx > 0) {
                onPrevious();
            } else {
                onNext();
            }
        },
    };
}

/** Elements that already do something with a sideways finger. */
function startsOnOwnGesture(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
        return false;
    }

    return (
        target.closest(
            'input, textarea, select, [contenteditable="true"], [data-swipe="off"]',
        ) !== null
    );
}
